import { Button, Grid, Paper, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Dispatch, SetStateAction, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { ConfirmCancellationDialog } from "common/components";
import { useTranslation } from "common/locales";
import { useAppStore } from "stores";
import { StyledWizardAppBar } from ".";

const StyledPaper = styled(Paper)(() => ({
  margin: "auto",
  maxWidth: "400px",
  padding: "50px",
}));

interface Props {
  dateTimeMeasurementStart: Date;
  setDateTimeMeasurementStart: Dispatch<SetStateAction<Date>>;
  dateTimeMeasurementEnd: Date;
  setDateTimeMeasurementEnd: Dispatch<SetStateAction<Date>>;
}

export default function EnterTime({
  dateTimeMeasurementStart,
  setDateTimeMeasurementStart,
  dateTimeMeasurementEnd,
  setDateTimeMeasurementEnd,
}: Props) {
  const [finishTimeError, setFinishTimeError] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleDateChange = (d: Date | null) => {
    if (d) {
      setDateTimeMeasurementStart(d);
      setDateTimeMeasurementEnd(d);
    }
  };
  const handleStartTimeChange = (d: Date | null) => {
    // we want to allow a start time to be changed any time, but set the error if the finish time is before start time
    if (d) {
      setDateTimeMeasurementStart(d);
      if (d < dateTimeMeasurementEnd) {
        setFinishTimeError(false);
      } else {
        setFinishTimeError(true);
      }
    }
  };
  const handleFinishTimeChange = (d: Date | null) => {
    if (d && d > dateTimeMeasurementStart) {
      setFinishTimeError(false);
      setDateTimeMeasurementEnd(d);
    } else {
      setFinishTimeError(true);
    }
  };
  const currentFarmId = useAppStore((state) => state.currentFarmId);
  const { formatMessage: f } = useIntl();

  const handleCancel = () => {
    // For the cover flow, just navigate back directly since this is the first step
    // and users haven't entered significant data yet
    navigate(`/farm/${currentFarmId || ""}`);
  };

  const handleConfirmationClose = () => {
    setConfirmCancelOpen(false);
  };

  return (
    <>
      <Grid container spacing={3} sx={{ marginBottom: { xs: "100px", sm: 0 } }}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <Typography variant="h5">
            {t("addCovers.enterTime.heading")}
          </Typography>
          <Typography variant="h6">
            {t("addCovers.enterTime.subheading")}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <StyledPaper>
              <DatePicker
                disableFuture
                format="MMMM do"
                label={t("addCovers.enterTime.dateLabel")}
                value={dateTimeMeasurementStart}
                slotProps={{
                  textField: {
                    variant: "standard",
                    fullWidth: true,
                  },
                }}
                onChange={handleDateChange}
                DialogProps={{
                  /*https://github.com/mui/material-ui/issues/26664*/
                  PaperProps: {
                    sx: { "& *:focus": { outline: "none" } },
                  },
                }}
              />
              <TimePicker
                ampm={false}
                label={t("addCovers.enterTime.startTimeLabel")}
                value={dateTimeMeasurementStart}
                views={["hours", "minutes"]}
                onChange={handleStartTimeChange}
                slotProps={{
                  textField: {
                    variant: "standard",
                    fullWidth: true,
                  },
                }}
                DialogProps={{
                  /*https://github.com/mui/material-ui/issues/26664*/
                  PaperProps: {
                    sx: { "& *:focus": { outline: "none" } },
                  },
                }}
              />
              <TimePicker
                ampm={false}
                label={t("addCovers.enterTime.endTimeLabel")}
                value={dateTimeMeasurementEnd}
                views={["hours", "minutes"]}
                onChange={handleFinishTimeChange}
                slotProps={{
                  textField: {
                    variant: "standard",
                    fullWidth: true,
                    error: finishTimeError,
                    helperText: finishTimeError
                      ? t("addCovers.enterTime.invalidFinishTime")
                      : "",
                  },
                }}
                DialogProps={{
                  /*https://github.com/mui/material-ui/issues/26664*/
                  PaperProps: {
                    sx: { "& *:focus": { outline: "none" } },
                  },
                }}
              />
            </StyledPaper>
          </LocalizationProvider>
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}></Grid>
      </Grid>
      <StyledWizardAppBar>
        <Button color="secondary" onClick={handleCancel}>
          {f({ id: "addCovers.cancel.label" })}
        </Button>
        <Button
          onClick={() => {
            navigate(`/add-covers/enter-covers`);
          }}
          color="primary"
          disabled={finishTimeError}
        >
          {f({ id: "addCovers.next.label" })}
        </Button>
      </StyledWizardAppBar>
      <ConfirmCancellationDialog
        open={confirmCancelOpen}
        onClose={handleConfirmationClose}
      />
    </>
  );
}
